/** @jsxImportSource @emotion/react */
import { Link } from '@mui/material';
import { motion, MotionConfig } from 'framer-motion';
import React, {
  ComponentProps,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { isChatAvailable, useScrollInfo } from '../../../helpers';
import Button from '../../UI/Button';
import NavRoot, { NavLink, NavRootProps } from '../NavRoot';
import LinkDrawer from './LinkDrawer';
import CartIcon from './cartIcon';

import { SerializedStyles } from '@emotion/react';
import { openChat } from '@shared/chat-js';
import Imgix from 'react-imgix';
import { cn } from '../../../lib';
import { Minus } from '../../Icons';
import { SmoothMount } from '../../UI';
import { DrawerProvider, useDrawer } from '../../UI/Drawer';
import callIcon from '../../svgs/callIcon.svg';
import chatIcon from '../../svgs/chatIcon.svg';
import chatOfflineIcon from '../../svgs/chatOfflineIcon.svg';
import customerPortalIcon from '../../svgs/customerPortalIcon.svg';
import Minimal from './Minimal';
import MobileProductsNav from './MobileProductsNav';
import MobileServicesNav from './MobileServicesNav';
import { NavDrawerProps } from './NavDrawer';
import ProductsNav from './ProductsNav';
import { ILink, links } from './links';
import { useTranslation } from 'react-i18next';
import { Drawer } from '@shared/cove-ui';
export interface NavProps extends NavRootProps {
  cartCount: number;
  showCart: () => void;
  isMinimal?: boolean;
  css?: SerializedStyles;
  clickToCall?: boolean | { hideChat?: boolean };
  callNumber?: string;
  showShopButton?: boolean;
  navLinks?: NavLink[];
  navDrawers?: NavDrawerProps[];
  hideCart?: boolean;
}

const MobileNavCTA = ({ cartCount = 0, showShopButton = true }) => {
  const { t } = useTranslation('common');
  const { top = 0 } = useScrollInfo();
  const isAtTop = top === 0;
  const [mounted, setIsMounted] = useState(false);
  const hasCart = cartCount > 0;

  useEffect(() => {
    if (!mounted) {
      setIsMounted(true);
    }
  }, []);

  return (
    <div className="gap-cove-5 flex items-center">
      {isAtTop && mounted ? (
        <Button
          style="container"
          cypressId="nav-shop-button"
          id="nav-link-to-quiz"
          href={hasCart ? '/quiz-results' : '/quiz'}
          data-type="button"
          className={`shrink-0 text-sm sm:text-base ${showShopButton && 'inline-flex'}`}
        >
          {hasCart
            ? t('my-system', { defaultValue: 'My System' })
            : t('build-my-system', { defaultValue: 'Build My System' })}
        </Button>
      ) : (
        <Button
          style="outlined"
          cypressId="nav-shop-button"
          id="nav-link-to-quiz"
          href={hasCart ? '/quiz-results' : '/quiz'}
          data-type="button"
          className={`shrink-0 text-sm sm:text-base ${showShopButton && 'inline-flex'}`}
        >
          {hasCart
            ? t('my-system', { defaultValue: 'My System' })
            : t('build-my-system', { defaultValue: 'Build My System' })}
        </Button>
      )}
      <Drawer.Trigger>
        <Button
          cypressId="nav-link-to-cart"
          id="nav-link-to-cart"
          data-type="button"
          variant="text"
          aria-label="Cart"
        >
          <CartIcon count={cartCount} />
        </Button>
      </Drawer.Trigger>
    </div>
  );
};

const mobileNavSectionVariants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: {
      delayChildren: 0.125,
      staggerChildren: 0.075,
    },
  },
};

const mobileNavSectionVariantsChild = {
  initial: { opacity: 0, scale: 0.93 },
  animate: { opacity: 1, scale: 1 },
};

const Nav: React.FC<NavProps> = ({
  cartCount,
  showCart,
  isMinimal = false,
  clickToCall = false,
  callNumber,
  navLeft,
  navRight,
  showShopButton = true,
  navLinks,
  navDrawers,
  ...props
}) => {
  const { t } = useTranslation('common');
  const chatOffline = !isChatAvailable();
  const { setDrawerOpen } = useDrawer();

  const getDisplayNumber = (phone: string) => {
    const first = phone.slice(1, 4);
    const second = phone.slice(4, 7);
    const third = phone.slice(7, 11);

    return `${first}.${second}.${third}`;
  };

  const CallChatCTA = ({ hideChat = false }: { hideChat?: boolean }) => {
    return (
      <>
        {!hideChat && (
          <Button
            size="small"
            style="outlined"
            color="purple"
            chatButton
            data-type="button"
            className="w-[146px]"
          >
            {t('chat', { defaultValue: 'Chat' })}
          </Button>
        )}

        <Button
          size="small"
          style="container"
          color="purple"
          href={`tel:${callNumber}`}
          data-type="button"
          className="w-[146px]"
        >
          {getDisplayNumber(callNumber || '')}
        </Button>
      </>
    );
  };

  const CallChatCTAMobile = (
    <>
      <Button
        size="small"
        style="container"
        color="purple"
        href={`tel:${callNumber}`}
        data-type="button"
      >
        {getDisplayNumber(callNumber || '')}
      </Button>
    </>
  );

  const { links, linkDrawers } = useMemo(() => {
    const collapseNavLinks = [
      {
        name: 'v1-nav-drawer-products',
        label: t('products', { defaultValue: 'Products' }),
        to: '/products/',
        hasDrawer: true,
        hideMobile: true,
        DrawerComponent: () => <MobileProductsNav />,
      },
      {
        name: 'v1-nav-drawer-services',
        label: t('services', { defaultValue: 'Services' }),
        to: '/services',
        hasDrawer: true,
        hideMobile: true,
        DrawerComponent: () => <MobileServicesNav />,
      },
    ];

    const links = isMinimal
      ? [
          {
            name: 'v1-nav-drawer-minimal',
            label: ``,
            to: '#',
            hasDrawer: false,
            hideMobile: true,
            Component: Minimal,
          },
        ]
      : navLinks
        ? navLinks
        : [
            {
              name: 'v1-nav-drawer-sign-in',
              label: 'Sign In',
              to: '#',
              hasDrawer: false,
              hideDivider: true,
              hideDesktop: true,
              Component: () => {
                const { drawerOpen } = useDrawer();
                return (
                  <motion.div
                    variants={mobileNavSectionVariants}
                    className="px-cove-25 pb-cove-50 flex w-full flex-col"
                    initial="initial"
                    animate={drawerOpen ? 'animate' : 'initial'}
                  >
                    {mobileNavLinks
                      .map(link => {
                        const translatedTitle = t(
                          `${link.translationKey}.title`,
                          { defaultValue: link.title }
                        );
                        const translatedDescription = t(
                          `${link.translationKey}.description`,
                          { defaultValue: link.description }
                        );
                        return {
                          ...link,
                          title: translatedTitle,
                          description: translatedDescription,
                        };
                      })
                      .map(link => (
                        <MobileNavLinkDropdown key={link.title} {...link} />
                      ))}
                    <div className="py-cove-25 flex w-full justify-center md:justify-start">
                      <Button href="/quiz" size="large" color="purple">
                        {t('build-my-system', {
                          defaultValue: 'Build My System',
                        })}
                      </Button>
                    </div>
                    <div className="grid w-full grid-cols-2">
                      <div>
                        <Button
                          style="text"
                          className={cn(
                            'bg-cove-white text-cove-blue flex !min-h-[54px] w-full items-center justify-start p-0 text-base font-normal leading-[30px]',
                            chatOffline && 'text-cove-dove'
                          )}
                          disabled={chatOffline}
                          onClick={() => {
                            openChat();
                            setDrawerOpen?.(false);
                          }}
                          aria-label={`Chat ${
                            chatOffline
                              ? 'We are not online at the moment. Chat hours are 9AM—6PM Mon-Fri MST.'
                              : ''
                          }`}
                          cypressId="nav-chat-button"
                        >
                          <img
                            src={chatOffline ? chatOfflineIcon : chatIcon}
                            alt="chat icon"
                          />
                          {t('chat', { defaultValue: 'Chat' })}
                          {chatOffline && (
                            <div className="ml-[14px] text-left text-[14px] font-extralight leading-[18px]">
                              <b>We are not online at the moment.</b> Chat hours
                              are 9AM—6PM Mon-Fri MST.
                            </div>
                          )}
                        </Button>
                      </div>
                      <div className="flex w-full justify-end">
                        <a
                          id="nav-link-to-customer-portal"
                          href="https://portal.covesmart.com"
                          className="bg-cove-white text-cove-blue flex !min-h-[54px] items-center justify-start p-0 text-right text-base font-normal"
                          rel="nofollow"
                        >
                          <img src={customerPortalIcon} alt="sign in" />
                          {t('sign-in', { defaultValue: 'Sign In' })}
                        </a>
                      </div>
                    </div>
                    <div className="border-t-cove-dove grid w-full grid-cols-2 border-t">
                      <div>
                        <a
                          id="nav-link-to-call"
                          href="tel:8552683669"
                          className="bg-cove-white text-cove-blue flex !min-h-[54px] w-full items-center justify-start p-0 text-base font-normal leading-[30px]"
                        >
                          <img src={callIcon} alt="call icon" />
                          {t('call', { defaultValue: 'Call' })}
                        </a>
                      </div>
                      <div className="flex w-full justify-end">
                        <a
                          href="/refer-and-earn"
                          className="bg-cove-white text-cove-blue flex !min-h-[54px] items-center justify-start p-0 text-right text-base font-normal"
                        >
                          {t('refer-and-earn', {
                            defaultValue: 'Refer and earn',
                          })}
                        </a>
                      </div>
                    </div>
                    <div className="border-t-cove-dove grid w-full grid-cols-2 border-t">
                      <div>
                        <a
                          id="nav-link-to-reviews"
                          href="/reviews"
                          className="bg-cove-white text-cove-blue flex !min-h-[54px] w-full items-center justify-start p-0 text-base font-normal leading-[30px]"
                        >
                          {t('links.company.reviews', {
                            defaultValue: 'Reviews',
                          })}
                        </a>
                      </div>
                      <div className="flex w-full justify-end">
                        <a
                          href="/resources"
                          className="bg-cove-white text-cove-blue flex !min-h-[54px] items-center justify-start p-0 text-right text-base font-normal"
                        >
                          {t('links.help.resources', {
                            defaultValue: 'Resources',
                          })}
                        </a>
                      </div>
                    </div>
                  </motion.div>
                );
              },
            },

            ...collapseNavLinks,
            {
              name: 'v1-nav-drawer-reviews',
              label: t('reviews', { defaultValue: 'Reviews' }),
              to: '/reviews/',
              hideDesktop: false,
              hasDrawer: false,
              hideMobile: true,
            },
            {
              name: 'v1-nav-drawer-resources',
              label: t('help-center', { defaultValue: 'Help Center' }),
              to: 'https://support.covesmart.com/',
              hideDesktop: false,
              hasDrawer: false,
              hideMobile: true,
            },
            {
              name: 'v1-nav-drawer-contact',
              label: t('contact', { defaultValue: 'Contact' }),
              to: '#',
              hasDrawer: true,
              hideMobile: true,
            },
            {
              name: 'v1-nav-drawer-sign-in',
              label: t('sign-in', { defaultValue: 'Sign In' }),
              to: '#',
              hasDrawer: false,
              hideMobile: true,
              Component: () => {
                return (
                  <>
                    <Link
                      id="nav-link-to-customer-portal"
                      href="https://portal.covesmart.com"
                      underline="none"
                      className="whitespace-nowrap text-base font-light"
                    >
                      {t('sign-in', { defaultValue: 'Sign In' })}
                    </Link>
                  </>
                );
              },
            },
            {
              name: 'v1-nav-drawer-shop',
              label: t('shop', { defaultValue: 'Shop' }),
              to: '#',
              hasDrawer: false,
              hideMobile: true,
              Component: () => {
                return <MobileNavCTA cartCount={cartCount} />;
              },
            },
          ];

    const linkDrawers = !!navDrawers
      ? navDrawers
      : [
          {
            name: 'v1-nav-drawer-products',
            withStyle: true,
            Component: () => {
              return <ProductsNav />;
            },
          },
          {
            name: 'v1-nav-drawer-services',
            withStyle: false,
            Component: () => {
              return (
                <LinkDrawer
                  name="v1-nav-drawer-services"
                  rows={1}
                  size="small"
                  offset
                />
              );
            },
          },
          {
            name: 'v1-nav-drawer-contact',
            withStyle: false,
            Component: () => {
              return (
                <LinkDrawer
                  name="v1-nav-drawer-contact"
                  rows={1}
                  size="small"
                  offset
                  chatOffline={chatOffline}
                />
              );
            },
          },
        ];
    return { links, linkDrawers };
  }, [cartCount, chatOffline]);

  const clickToCallLinks = (props: boolean | { hideChat?: boolean }) => {
    const hideChat = typeof props === 'object' && props.hideChat;

    return [
      {
        name: 'v1-nav-click-to-call',
        label: 'Collapse',
        to: '#',
        Component: () => {
          return (
            <div className="gap-cove-35 pl-cove-25 pr-cove-50 flex">
              <CallChatCTA hideChat={hideChat} />
            </div>
          );
        },
        hasDrawer: false,
        hideDivider: true,
        hideDesktop: false,
      },
    ];
  };

  const hasClickToCall =
    (typeof clickToCall === 'boolean' && clickToCall) ||
    (typeof clickToCall === 'object' &&
      typeof clickToCall.hideChat === 'boolean');

  return (
    <NavRoot
      navLinks={hasClickToCall ? clickToCallLinks(clickToCall) : links}
      linkDrawers={clickToCall ? [] : linkDrawers}
      cartCount={cartCount}
      showCart={showCart}
      navLeft={navLeft}
      navRight={navRight}
      mobileNavCTA={
        clickToCall ? (
          CallChatCTAMobile
        ) : isMinimal ? (
          <></>
        ) : (
          <MobileNavCTA
            cartCount={cartCount}
            showCart={showCart}
            showShopButton={showShopButton}
          />
        )
      }
      {...props}
    />
  );
};

const FinalNav = ({ ...props }: ComponentProps<typeof Nav>) => {
  return (
    <MotionConfig transition={{ duration: 0.3, type: 'spring', bounce: 0 }}>
      <DrawerProvider>
        <Nav {...props} />
      </DrawerProvider>
    </MotionConfig>
  );
};

type MobileNavLink = {
  image: {
    url: string;
    alt: string;
  };
  translationKey: string;
  title: string;
  description: string;
  DropdownComponent?: ReactNode;
  link?: string;
};

const linksVariantsWrapper = {
  initial: { opacity: 0, y: 10 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      staggerChildren: 0.035,
    },
  },
};

const linksVariants = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
};

const MobileNavLinkDropdownLink = ({ to, label, translationKey }: ILink) => {
  const { t } = useTranslation('common', { keyPrefix: 'links' });
  const translatedLabel = t(translationKey, { defaultValue: label });

  return (
    <motion.a
      href={to}
      className="text-cove-blue py-cove-15 px-0 text-base font-normal hover:underline"
      variants={linksVariants}
    >
      {translatedLabel}
    </motion.a>
  );
};

const mobileNavLinks: Array<MobileNavLink> = [
  {
    image: {
      url: 'https://www.datocms-assets.com/10154/1724780800-mobile-nav-products.png',
      alt: 'Person holding door sensor',
    },
    translationKey: 'links.mobile-dropdown.products',
    title: 'Products',
    description: 'Peace of mind you can count on.',
    DropdownComponent: (
      <motion.div
        variants={linksVariantsWrapper}
        initial="initial"
        animate="animate"
        className="py-cove-15 flex flex-col items-start justify-center"
      >
        <MobileNavLinkDropdownLink
          imageName=""
          id="all-products"
          translationKey="all-products"
          to="/products"
          label="All Products"
        />
        {links['v1-nav-drawer-home-security'].map(link => {
          return <MobileNavLinkDropdownLink {...link} key={link.to} />;
        })}
      </motion.div>
    ),
  },
  {
    image: {
      url: 'https://www.datocms-assets.com/10154/1724780890-mobile-nav-cameras.png',
      alt: 'Cove indoor camera on side table',
    },
    translationKey: 'links.mobile-dropdown.cameras',
    title: 'Cameras',
    description: 'Know what’s going on in and around your home.',
    DropdownComponent: (
      <motion.div
        variants={linksVariantsWrapper}
        initial="initial"
        animate="animate"
        className="py-cove-15 flex flex-col items-start justify-center"
      >
        {links['v1-nav-drawer-cameras'].map(link => {
          return <MobileNavLinkDropdownLink {...link} key={link.to} />;
        })}
      </motion.div>
    ),
  },
  {
    image: {
      url: 'https://www.datocms-assets.com/10154/1724780865-mobile-nav-environmental.png',
      alt: 'person installing smoke detector',
    },
    translationKey: 'links.mobile-dropdown.environmental',
    title: 'Environmental',
    description: 'Detect smoke, water leaks, and freezing pipes.',
    DropdownComponent: (
      <motion.div
        variants={linksVariantsWrapper}
        initial="initial"
        animate="animate"
        className="py-cove-15 flex flex-col items-start justify-center"
      >
        {links['v1-nav-drawer-environmental'].map(link => {
          return <MobileNavLinkDropdownLink {...link} key={link.to} />;
        })}
      </motion.div>
    ),
  },
  {
    image: {
      url: 'https://www.datocms-assets.com/10154/1724780876-mobile-nav-monitoring.png',
      alt: 'Alarm panel on side table',
    },
    translationKey: 'links.mobile-dropdown.monitoring',
    title: 'Monitoring',
    description: "Threats don't sleep. We don't either.",
    link: '/monitored-security/',
  },
];

const MobileNavLinkDropdown = ({ ...props }: MobileNavLink) => {
  const [open, setOpen] = useState(false);
  const hasLink = !!props.link;

  return (
    <motion.div
      variants={mobileNavSectionVariantsChild}
      className="border-b-cove-dove first:border-t-cove-dove py-cove-10 w-full border-b first:border-t"
    >
      {hasLink ? (
        <a
          className="gap-cove-15 xs:grid-cols-[134px_1fr] text-cove-navy-blue relative grid w-full grid-cols-[75px_1fr] items-center"
          href={props.link}
          aria-label={`${props.title}`}
        >
          <MobileNavLinkDropdownContent {...props} />
        </a>
      ) : (
        <>
          <button
            className="gap-cove-15 xs:grid-cols-[134px_1fr] relative grid w-full grid-cols-[75px_1fr] items-center bg-transparent"
            type="button"
            onClick={() => setOpen(!open)}
            aria-label={`${props.title}`}
          >
            <MobileNavLinkDropdownContent {...props} open={open} />
          </button>
          <SmoothMount show={open} fade>
            {props.DropdownComponent}
          </SmoothMount>
        </>
      )}
    </motion.div>
  );
};

const MobileNavLinkDropdownContent = ({
  image,
  title,
  description,
  link,
  open,
}: MobileNavLink & { open?: boolean }) => {
  const hasLink = !!link;
  return (
    <>
      <div className="xs:max-w-[134px] h-auto w-full max-w-[89px] *:h-auto *:w-full">
        {/* @ts-expect-error - Imgix type is incorrect */}
        <Imgix
          src={image.url}
          width={134}
          height={134}
          htmlAttributes={{ alt: image.alt }}
        />
      </div>
      <div className="gap-cove-5 xs:gap-cove-15 pr-cove-25 flex w-full flex-col items-start justify-center text-left">
        <p className="m-0 text-[20px] font-bold leading-[25px]">{title}</p>
        <p className="m-0 text-sm">{description}</p>
      </div>
      {!hasLink && (
        <div className="w-cove-35 h-cove-35 absolute -right-2">
          <div className="w-cove-35 h-cove-35 relative">
            <Minus className="absolute inset-0" />
            <Minus
              className={cn(
                'absolute inset-0 transition-all',
                !open && 'rotate-90'
              )}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default FinalNav;
